import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import FeedbackDashboardCard, {
  FeedbackDashboardCardProps,
} from 'shared/components/Feedback/FeedbackDashboardCard';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  FeedbackApi,
  FeedbackStatus,
  feedbackMapFromApi,
  getFeedback,
} from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import SectionFeedback from './SectionFeedback';
import styled from 'styled-components';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  User,
  UserType,
  getCurrentUserFullName,
  getFeedbackView,
} from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import FeedbackReport, { FeedbackReportHandler } from './FeedbackReport';
import NPOFeedback from 'npo/pages/NPOFeedback';
import {
  allFeedbackSectionsWithCustomQuestions,
  allFeedbackSectionsWithCustomQuestionsAndAllocations,
} from 'npo/pages/NPOFeedback/utils';
import FeedbackAwaiting from './FeedbackAwaiting';
import { AdminModalContentProps } from 'npo/pages/ReportsBeta/ProgramModal/AdminModalContent';
import AdminModal from 'npo/pages/ReportsBeta/ProgramModal/AdminModal';
import { useCustomQuestions } from 'admin/pages/ManageAccounts/CustomQuestions/hook';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import {
  BetaFeedbackWithProvidersAndReportId,
  FeedbackRender,
} from 'npo/pages/BetaFeedback';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { ReportBuilderData } from 'shared/providers/ReportProvider/utils';

const newFeedbackViewState: FeedbackStatus[] = [
  'Draft',
  'Interim Updates Needed',
  'Final Updates Needed',
  'Awaiting TI Verification',
  'Awaiting Funder Verification',
  'TI Feedback Provided',
  'Funder Feedback Provided',
  'Published',
  'Finalized',
];

interface FeedbackProps {
  userType?: UserType;
}

const StyledBreadcrumbDiv = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${SPACERS.small};
  .label {
    margin-bottom: 0;
  }
  .icon {
    margin-right: ${SPACERS.tiny};
    margin-left: ${SPACERS.tiny};
  }
`;

const Breadcrumb: React.FC<FeedbackProps> = ({ userType }) =>
  userType === 'npo' ? null : (
    <StyledBreadcrumbDiv className="breadcrumb">
      <TrueImpactText variant="heading-label" className="label">
        Report Dashboard
      </TrueImpactText>
      <FontAwesomeIcon icon={faChevronRight} className="icon" />
      <TrueImpactText variant="heading-label" className="label">
        Report Feedback
      </TrueImpactText>
    </StyledBreadcrumbDiv>
  );

const StyledDiv = styled.div`
  .feedback-grid {
    display: flex;
    min-height: calc(100vh - 350px);
    .report-grid {
      flex: 1;
      background-color: ${COLORS.grayLightest};
    }
    .section-grid {
      flex: 1;
      min-height: calc(100vh - 350px);
    }
  }
`;

const StyledError = styled(TrueImpactText)`
  padding: ${SPACERS.large};
`;

const Feedback: React.FC<FeedbackProps> = ({ userType = 'ti staff' }) => {
  const trackEvent = useTrackEvent();
  const client = useClient();
  const {
    user,
    userId,
    email,
    type,
    isAdmin,
    isDonor,
    isNPO,
  } = useUserActions();
  const { report, isReportEndDatePassed } = useReport();
  const { loadReportIndicators } = useIndicator();
  const ref = useRef<FeedbackReportHandler>(null);
  const feedbackId: string = useParams<{ feedbackId: string }>().feedbackId;
  const [showStatusModal, setShowStatusModal] = useState<
    AdminModalContentProps['report'] | undefined
  >(undefined);
  const userAttributes = (user.attributes as unknown) as User | undefined;

  useEffect(() => {
    loadReportIndicators(feedbackId);
  }, [feedbackId]);

  useEffect(() => {
    const reportData = report.data;
    if (!reportData || !reportData.stage) return;
    trackEvent(
      'Feedback',
      pullReportData(reportData, isReportEndDatePassed, userType),
    );
  }, [report]);

  const currentUserId = (
    userId || userAttributes?.['custom:user_id']
  )?.toString();
  const currentUserEmail = email || userAttributes?.email || undefined;
  const currentUserType: UserType = isNPO
    ? 'npo'
    : isDonor
    ? 'donor'
    : 'ti staff';
  const currentUserName = getCurrentUserFullName(userAttributes);
  const feedbackSections =
    userType === 'ti staff' || userType === 'donor'
      ? allFeedbackSectionsWithCustomQuestionsAndAllocations
      : allFeedbackSectionsWithCustomQuestions;

  const [feedbackRequestState, makeRequest] = useGraphQLRequest<{
    reportsTodo: FeedbackApi[];
  }>();
  const [feedback, setFeedback] = useState<FeedbackDashboardCardProps>();
  const { customQuestionsWithoutGuidance } = useCustomQuestions({
    reportId: feedbackId,
  });

  const handleStatusClosed = useCallback(() => {
    setShowStatusModal(undefined);
  }, [setShowStatusModal]);

  const refreshFeedbackStatus = useCallback(
    (status: FeedbackStatus) => {
      setFeedback(
        s =>
          s && {
            ...s,
            status,
          },
      );
    },
    [setFeedback],
  );

  useEffect(() => {
    document.body.style.backgroundColor = COLORS.whiteBase;
    return () => {
      document.body.style.backgroundColor = COLORS.whiteLight;
    };
  }, []);

  // Apply CN theme for CN reports
  useEffect(() => {
    if (feedbackRequestState.type === 'REQUEST_SUCCESS') {
      const report = feedbackRequestState.data.reportsTodo[0];
      const root = document.getElementById('root');
      if (root && report?.cnOptedIn) {
        root.classList.add('cnTheme');
      }
      return () => {
        if (root) {
          root.classList.remove('cnTheme');
        }
      };
    }
  }, [feedbackRequestState.type, feedbackRequestState.data]);

  useEffect(() => {
    makeRequest(
      getFeedback(
        client!,
        undefined,
        undefined,
        feedbackId,
        undefined,
        undefined,
      ),
    );
  }, [feedbackId]);

  useEffect(() => {
    if (feedbackRequestState.type === 'REQUEST_SUCCESS') {
      const feedbacks = feedbackMapFromApi(
        feedbackRequestState.data.reportsTodo,
      ).feedbackRows;
      setFeedback(feedbacks[0]);
    }
  }, [feedbackRequestState.type, setFeedback]);

  switch (feedbackRequestState.type) {
    case 'REQUEST_INIT':
    case 'REQUEST_START':
      return <TrueImpactLoading />;
    case 'REQUEST_SUCCESS':
      if (feedback) {
        const report = feedbackRequestState.data.reportsTodo[0];

        if (newFeedbackViewState.includes(report.status)) {
          return (
            <BetaFeedbackWithProvidersAndReportId
              reportId={feedback.reportId}
            />
          );
        }
        const feedbackView = getFeedbackView(
          feedback.status,
          report.feedbackType,
        );
        return (
          <>
            {showStatusModal && (
              <AdminModal
                open={true}
                handleClose={handleStatusClosed}
                report={showStatusModal}
              />
            )}
            <FeedbackRender
              topHeight={52}
              topComponent={
                <FeedbackDashboardCard
                  margin="0 auto"
                  {...feedback}
                  breadcrumb={<Breadcrumb userType={userType} />}
                  onReportStatusClick={() =>
                    setShowStatusModal({
                      dbId: feedback.id,
                      stage: feedback.stage,
                      status: feedback.status,
                      year: Number(feedback.year),
                      feedbackType: feedback.feedbackType,
                      reportingType: feedback.reportingType || 'FULL',
                      optedInFullReporting: feedback.optedInFullReporting,
                    })
                  }
                />
              }
              leftComponent={
                <FeedbackReport
                  ref={ref}
                  feedback={feedback}
                  programName={feedback.title || feedback.portfolioName}
                  currentUserType={currentUserType}
                />
              }
              rightComponent={() => (
                <>
                  {feedbackView === 'verification' && (
                    <NPOFeedback
                      report={report}
                      feedbackReportRef={ref}
                      programName={feedback.title}
                      reportId={feedback.reportId}
                      feedbackStatus={feedback.status}
                      currentUserId={currentUserId}
                      currentUserEmail={currentUserEmail || undefined}
                      userName={currentUserName}
                      userType={currentUserType}
                      customQuestions={customQuestionsWithoutGuidance}
                      allFeedbackSections={feedbackSections}
                      refreshFeedbackStatus={refreshFeedbackStatus}
                      className="section-grid"
                    />
                  )}
                  {feedbackView === 'awaiting' && (
                    <FeedbackAwaiting
                      userType={currentUserType}
                      feedbackStatus={feedback.status}
                      className="section-grid"
                    />
                  )}
                  {feedbackView === 'feedback' && (
                    <SectionFeedback
                      reportId={feedback.reportId}
                      feedbackStatus={feedback.status}
                      report={report}
                      programName={feedback.title}
                      currentUserId={currentUserId}
                      currentUserEmail={currentUserEmail}
                      userName={currentUserName}
                      userType={currentUserType}
                      customQuestions={customQuestionsWithoutGuidance}
                      allFeedbackSections={feedbackSections}
                      refreshFeedbackStatus={refreshFeedbackStatus}
                      className="section-grid"
                    />
                  )}
                </>
              )}
            />
          </>
        );
      }
      // falls through
    default:
    case 'REQUEST_ERROR':
      return (
        <StyledError variant="body-bold">
          Unable to find the feedback
        </StyledError>
      );
  }
};

export default Feedback;
